/** One section on the model board: its header (name, tab, order) and its fields, which can be dragged. */
import { ArrowDown, ArrowUp, GripVertical, Plus, X } from 'lucide-react';
import { metadataTabs, sectionTabId, type MetadataSchemaDefinition } from '@prism/contracts';
import { TYPE_LABELS } from './FieldInspector';
import { dropSectionAt, moveField, moveSection, removeSection } from './modelDraftOps';
import type { ModelDnd } from './useModelDnd';

type Section = MetadataSchemaDefinition['sections'][number];

export function SectionCard({ s, def, dnd, selected, onSelect, onAddField, update, isNew, changed, usage }: {
  s: Section; def: MetadataSchemaDefinition; dnd: ModelDnd; selected: string | null; onSelect: (fieldId: string) => void; onAddField: (sectionId: string) => void;
  update: (fn: (d: MetadataSchemaDefinition) => void) => void; isNew: (id: string) => boolean; changed: Set<string | undefined>; usage: Record<string, number>;
}) {
  const { drag, drop, setDrag, finishDrag, performDrop, over } = dnd;
  const modelTabs = metadataTabs(def);
  const firstSection = def.sections[0]?.id;
  const sectionFields = def.fields.filter((f) => (f.section ?? firstSection) === s.id);
  const tabOf = sectionTabId(def, s);
  const sectionsInTab = def.sections.filter((x) => (tabOf ? sectionTabId(def, x) === tabOf : true));
  const pos = sectionsInTab.indexOf(s);
  const cls = [
    'model-section',
    drag?.kind === 'section' && drag.id === s.id ? 'dragging' : '',
    drop?.kind === 'field-end' && drop.sectionId === s.id ? 'drop-into' : '',
    (drop?.kind === 'section-before' || drop?.kind === 'section-after') && drop.id === s.id ? `drop-${drop.kind === 'section-before' ? 'before' : 'after'}` : '',
  ].join(' ');
  return (
    <section className={cls} data-section={s.id} aria-label={s.label}
      onDragOver={(e) => {
        over(e, 'field', () => ({ kind: 'field-end', sectionId: s.id }));
        over(e, 'section', (below) => ({ kind: below ? 'section-after' : 'section-before', id: s.id }));
      }}
      onDrop={performDrop}>
      <div className="model-section-head">
        <span className="drag-handle" draggable aria-hidden="true" title="Drag to move this section to another tab or position"
          onDragStart={(e) => {
            e.stopPropagation();
            setDrag({ kind: 'section', id: s.id });
            e.dataTransfer.effectAllowed = 'move';
            e.dataTransfer.setData('text/plain', s.id);
            const card = e.currentTarget.closest('.model-section');
            if (card) e.dataTransfer.setDragImage(card, 12, 12);
          }}
          onDragEnd={finishDrag}><GripVertical size={14} /></span>
        <input className="section-title-input" value={s.label} aria-label="Section name"
          onChange={(e) => update((d) => { d.sections.find((x) => x.id === s.id)!.label = e.target.value; })} />
        {modelTabs.length > 1 && (
          <select className="speed-select section-tab-select" aria-label="Tab for this section" data-testid="section-tab" value={sectionTabId(def, s) ?? ''}
            onChange={(e) => update((d) => dropSectionAt(d, s.id, { kind: 'section-end', tabId: e.target.value }))}>
            {modelTabs.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
          </select>
        )}
        <button className="btn ghost icon sm" aria-label="Move section up" disabled={pos <= 0} onClick={() => update((d) => moveSection(d, s.id, -1))}><ArrowUp size={13} /></button>
        <button className="btn ghost icon sm" aria-label="Move section down" disabled={pos >= sectionsInTab.length - 1} onClick={() => update((d) => moveSection(d, s.id, 1))}><ArrowDown size={13} /></button>
        {sectionFields.length === 0 && def.sections.length > 1 && (
          <button className="btn ghost icon sm" aria-label="Remove empty section" onClick={() => update((d) => removeSection(d, s.id))}><X size={13} /></button>
        )}
      </div>
      <div className="model-section-fields" role="list" aria-label={`${s.label} fields`}>
        {sectionFields.map((f) => {
          const dropCls = (drop?.kind === 'field-before' || drop?.kind === 'field-after') && drop.id === f.id ? `drop-${drop.kind === 'field-before' ? 'before' : 'after'}` : '';
          return (
            <div key={f.id} role="listitem" tabIndex={0} draggable
              className={`model-field ${selected === f.id ? 'selected' : ''} ${drag?.kind === 'field' && drag.id === f.id ? 'dragging' : ''} ${dropCls}`}
              data-field={f.id}
              onClick={() => onSelect(f.id)}
              onKeyDown={(e) => {
                if (e.target !== e.currentTarget) return;
                if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(f.id); }
                else if (e.altKey && e.key === 'ArrowUp') { e.preventDefault(); update((d) => moveField(d, f.id, -1)); }
                else if (e.altKey && e.key === 'ArrowDown') { e.preventDefault(); update((d) => moveField(d, f.id, 1)); }
              }}
              onDragStart={(e) => {
                e.stopPropagation();
                setDrag({ kind: 'field', id: f.id });
                e.dataTransfer.effectAllowed = 'move';
                e.dataTransfer.setData('text/plain', f.id);
              }}
              onDragEnd={finishDrag}
              onDragOver={(e) => over(e, 'field', (below) => (f.id === drag?.id ? null : { kind: below ? 'field-after' : 'field-before', id: f.id }))}
              onDrop={performDrop}>
              <span className="model-field-main">
                <span className="model-field-label">{f.label || <em className="faint">Unnamed</em>}{f.required && <span className="req" title="Required">*</span>}</span>
                <span className="model-field-meta">
                  {TYPE_LABELS[f.type]}
                  {f.required ? '' : f.needed ? ' · needed' : ''}
                  {f.inspector ? ' · inspector' : ''}
                  {f.facet && ' · facet'}{f.column && ' · column'}
                  {f.visibleWhen ? ' · conditional' : ''}
                  {f.viewRoles?.length ? ' · restricted' : ''}
                  {f.hidden ? ' · hidden' : ''}
                  {usage[f.id] ? ` · ${usage[f.id]} asset${usage[f.id] === 1 ? '' : 's'}` : ''}
                </span>
              </span>
              {isNew(f.id) ? <span className="pill accent">New</span> : changed.has(f.id) ? <span className="pill">Changed</span> : null}
            </div>
          );
        })}
        {sectionFields.length === 0 && <div className="model-section-empty faint">No fields. Drop one here.</div>}
      </div>
      <button className="btn ghost sm model-add-field" onClick={() => onAddField(s.id)}><Plus size={13} /> Field</button>
    </section>
  );
}
